import { useEffect, useRef, useState } from 'react';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { loadCashStatement } from '@/lib/cashStatement';

function StatementActions({
  accountId,
  organizationId,
  disabled,
}: {
  accountId: string;
  organizationId: string;
  disabled: boolean;
}) {
  const [working, setWorking] = useState<'pdf' | 'excel' | null>(null);
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [error, setError] = useState('');
  const operation = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      operation.current?.abort();
    };
  }, []);
  const run = async (kind: 'pdf' | 'excel') => {
    if (disabled || operation.current) return;
    const controller = new AbortController();
    operation.current = controller;
    setWorking(kind);
    setError('');
    setProgress({ loaded: 0, total: 0 });
    const active = () => mounted.current && !controller.signal.aborted;
    try {
      const statement = await loadCashStatement(
        accountId,
        organizationId,
        controller.signal,
        (loaded, total) => {
          if (active()) setProgress({ loaded, total });
        },
      );
      const documents = await import('@/utils/cashStatementDocuments');
      if (!active()) return;
      const fileName = documents.cashStatementFileName(statement);
      const output =
        kind === 'pdf'
          ? documents.buildCashStatementPdf(statement)
          : documents.buildCashStatementWorkbook(statement);
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (!active()) return;
      if (kind === 'pdf')
        (output as ReturnType<typeof documents.buildCashStatementPdf>).save(fileName + '.pdf');
      else
        documents.downloadCashStatementWorkbook(
          output as ReturnType<typeof documents.buildCashStatementWorkbook>,
          fileName,
        );
      toast.success(`${kind === 'pdf' ? 'PDF' : 'Excel'} account statement download started.`);
    } catch (failure) {
      if (active())
        setError(
          failure instanceof Error
            ? failure.message
            : 'The statement could not be exported. Please retry.',
        );
    } finally {
      if (operation.current === controller) {
        operation.current = null;
        if (mounted.current) setWorking(null);
      }
    }
  };
  const button =
    'inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 text-xs font-medium hover:bg-muted disabled:opacity-50';
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div className="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          aria-label="Export account statement as Excel"
          disabled={disabled || working !== null}
          onClick={() => void run('excel')}
          className={button}
        >
          {working === 'excel' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-4 w-4" />
          )}
          Excel
        </button>
        <button
          type="button"
          aria-label="Export account statement as PDF"
          disabled={disabled || working !== null}
          onClick={() => void run('pdf')}
          className={button}
        >
          {working === 'pdf' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          PDF
        </button>
      </div>
      {working && (
        <div className="flex max-w-sm flex-wrap items-center justify-end gap-2 text-xs">
          <p role="status">
            {progress.total
              ? `Loading ${progress.loaded} of ${progress.total} entries…`
              : 'Loading statement…'}
          </p>
          <button
            type="button"
            onClick={() => operation.current?.abort()}
            className="min-h-11 px-2 text-primary"
          >
            Cancel export
          </button>
        </div>
      )}
      {error && (
        <p
          role="alert"
          className="max-w-sm rounded-lg bg-destructive/10 p-2 text-xs text-destructive"
        >
          {error}
        </p>
      )}
    </div>
  );
}
export default function CashStatementExports({
  accountId,
  disabled = false,
}: {
  accountId?: string;
  disabled?: boolean;
}) {
  const { organization, profile, access, can } = useAuth();
  if (!can('financials.view')) return null;
  return (
    <StatementActions
      key={JSON.stringify([accountId, organization?.id, profile?.id, access?.access_version])}
      accountId={accountId || ''}
      organizationId={organization?.id || ''}
      disabled={disabled || !accountId || !organization?.id}
    />
  );
}
